// BOQ line costing — the MEP bill template's method (Settings sheet of the consultant's BOQ workbook).
// Mirrors backend/app/core/boq_costing.py; keep the two in step.
//
//   Instal Rate (Labour)      = ROUND(Unit Hour × Factor × labour gang rate, 2)
//   Landed Cost               = ROUND(Basic Rate × (1 + landed uplift), 2)
//   Accessories / Transport   = ROUND(Landed Cost × %, 2) each
//   Unit Rate Materials       = Landed + Accessories + Transport
//   Equipment & Tools         = ROUND(Labour × %, 2)
//   Prelims                   = ROUND((Labour + Equipment) × %, 2)
//   Unit Rate (selling)       = Unit Rate Materials × (1 + material margin) + (Labour + Equipment + Prelims) × (1 + labour margin)
//   Amount                    = Unit Rate × QTY
// Percentages are fractions (0.05 = 5%). ROUND is Excel's: half away from zero.

export const RATE_FIELDS = {
  unit_labour_rate: 4500,                          // labour gang rate, ₦ per man-hour
  landed_cost_uplift_rate: 0.08,                   // freight, duty, clearing
  installation_accessories_consumables_rate: 0.05, // % of landed cost
  transport_to_site_rate: 0.03,                    // % of landed cost
  equipment_and_tools_rate: 0.12,                  // % of labour
  prelim_site_mgmt_supevission_rate: 0.15,         // % of (labour + equipment)
  margin_markup_material_rate: 0.10,
  margin_markup_labour_rate: 0.20,
};

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Excel ROUND(v, 2): half away from zero, without the binary-float slip (1.005 → 1.01)
export const xround = (v) => {
  const n = Math.abs(num(v)) * 100;
  return (Math.sign(v) || 1) * Math.round(n + n * 1e-12 + 1e-9) / 100;
};

const hasRates = (row) => !!row && Object.keys(RATE_FIELDS).some(k => num(row[k]) > 0);

// The rates a line is priced with: its own; for a line that has none yet (new, or entered before rates were
// kept per line), those of another line of the same BOQ; failing that, the template's.
export const ratesFor = (row, siblings = []) => {
  const source = hasRates(row) ? row : siblings.find(hasRates);
  const out = {};
  Object.entries(RATE_FIELDS).forEach(([k, d]) => { out[k] = source ? num(source[k]) : d; });
  return out;
};

// Every derived figure of a line, keyed by BOQ item field. Inputs: budgeted_quantity, basic_rate_material,
// factor, unit_hour, and the rates.
export const costLine = (row, rates) => {
  const qty = num(row.budgeted_quantity);
  const basic = num(row.basic_rate_material);
  const labour = xround(num(row.unit_hour) * num(row.factor) * rates.unit_labour_rate);
  const landed = xround(basic * (1 + rates.landed_cost_uplift_rate));
  const accessories = xround(landed * rates.installation_accessories_consumables_rate);
  const transport = xround(landed * rates.transport_to_site_rate);
  const unitMat = landed + accessories + transport;
  const equipment = xround(labour * rates.equipment_and_tools_rate);
  const prelims = xround((labour + equipment) * rates.prelim_site_mgmt_supevission_rate);
  const labBlock = labour + equipment + prelims;
  const unitRate = unitMat * (1 + rates.margin_markup_material_rate) + labBlock * (1 + rates.margin_markup_labour_rate);
  return {
    install_rate_labour: labour,
    unit_labour: labour,
    total_labour: xround(labour * qty),
    landed_cost_materials: landed,
    installation_accessories_consumables: accessories,
    transport_to_site: transport,
    unit_rate_material: xround(unitMat),
    total_material_delivered_site: xround(unitMat * qty),
    equipment_and_tools: equipment,
    prelim_site_mgmt_supevission: prelims,
    total_unit_rate_mat_lab: xround(unitMat + labBlock),
    total_amount_mat_lab: xround((unitMat + labBlock) * qty),
    margin_markup_material_amount: xround(unitMat * qty * rates.margin_markup_material_rate),
    margin_markup_labour_amount: xround(labBlock * qty * rates.margin_markup_labour_rate),
    unit_rate: xround(unitRate),
    amount: xround(unitRate * qty),
  };
};

// A line ready to save: inputs as numbers (a cleared consultant quantity is "not given", not 0), the rates
// it is priced with, and every derived figure.
export const priceRow = (row, siblings = []) => {
  const rates = ratesFor(row, siblings);
  const cq = row.consultant_quantity;
  const inputs = {
    budgeted_quantity: num(row.budgeted_quantity),
    consultant_quantity: cq === '' || cq == null ? null : num(cq),
    basic_rate_material: num(row.basic_rate_material),
    factor: num(row.factor),
    unit_hour: num(row.unit_hour),
    price_source_amount: num(row.price_source_amount),
  };
  return { ...row, ...inputs, ...rates, ...costLine(inputs, rates) };
};
